import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import './MorphBackground.css';

const MorphBackground = ({ showText = true }) => {
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Refs for Three.js objects
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const materialRef = useRef(null);
  const clockRef = useRef(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const waterBuffersRef = useRef(null);
  const waterTextureRef = useRef(null);
  const waterSettingsRef = useRef(null);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  const mouseThrottleTimeRef = useRef(0);
  const animationFrameRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize water settings
    waterSettingsRef.current = {
  resolution: 128,  // Reduced from 256 for better performance
  damping: 0.913,
  tension: 0.02,
  rippleStrength: 0.2,
  mouseIntensity: 1.2,
  clickIntensity: 3.0,
  rippleRadius: 15,  // Adjusted for lower resolution
  splatForce: 100000,  // Adjusted for lower resolution
  splatThickness: 0.1,
  vorticityInfluence: 0.2,
  swirlIntensity: 0.2,
  pressure: 0.3,
  velocityDissipation: 0.08,
  densityDissipation: 1.0,
  displacementScale: 0.01
};

    const resolution = waterSettingsRef.current.resolution;

    // Initialize water buffers
    waterBuffersRef.current = {
  current: new Float32Array(resolution * resolution),
  previous: new Float32Array(resolution * resolution),
  velocity: new Float32Array(resolution * resolution * 2),
  vorticity: new Float32Array(resolution * resolution),
  pressure: new Float32Array(resolution * resolution)
};

for (let i = 0; i < resolution * resolution; i++) {
      waterBuffersRef.current.current[i] = 0.0;
      waterBuffersRef.current.previous[i] = 0.0;
      waterBuffersRef.current.velocity[i * 2] = 0.0;
      waterBuffersRef.current.velocity[i * 2 + 1] = 0.0;
      waterBuffersRef.current.vorticity[i] = 0.0;
      waterBuffersRef.current.pressure[i] = 0.0;
    }

    // Create water texture
    waterTextureRef.current = new THREE.DataTexture(
      waterBuffersRef.current.current,
      waterSettingsRef.current.resolution,
      waterSettingsRef.current.resolution,
  THREE.RedFormat,
  THREE.FloatType
);
    waterTextureRef.current.minFilter = THREE.LinearFilter;
    waterTextureRef.current.magFilter = THREE.LinearFilter;
    waterTextureRef.current.needsUpdate = true;

    // Setup Three.js scene
    sceneRef.current = new THREE.Scene();
    
    // Simple orthographic camera that maps 1:1 to clip space
    cameraRef.current = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    
    rendererRef.current = new THREE.WebGLRenderer({ antialias: true });
    rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    rendererRef.current.setPixelRatio(1); // Set to 1 to avoid coordinate mismatch
    containerRef.current.appendChild(rendererRef.current.domElement);

    clockRef.current = new THREE.Clock();

    // Create shader material
const vertexShader = `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

const fragmentShader = `
      uniform float u_time;
      uniform vec2 u_resolution;
      uniform vec3 u_color1;
      uniform vec3 u_color2;
      uniform vec3 u_color3;
      uniform vec3 u_background;
      uniform float u_speed;
      uniform sampler2D u_waterTexture;
      uniform float u_waterStrength;
      uniform float u_ripple_time;
      uniform vec2 u_ripple_position;
      uniform float u_ripple_strength;
      uniform sampler2D u_textTexture;
      uniform bool u_showText;
      uniform bool u_isMonochrome;
      uniform float u_audioLow;
      uniform float u_audioMid;
      uniform float u_audioHigh;
      uniform float u_audioOverall;
      uniform float u_audioReactivity;
      
      varying vec2 vUv;

      void main() {
        vec2 r = u_resolution;
        vec2 FC = gl_FragCoord.xy;
        
        // Use vUv for reliable centered coordinates
        vec2 uv = vUv;
        
        // Center the coordinate system: move origin to center (0.5, 0.5) -> (0, 0)
        vec2 centeredUV = uv - 0.5;
        
        // Apply aspect ratio correction
        float aspect = r.x / r.y;
        vec2 screenP;
        if (aspect >= 1.0) {
          // Landscape: stretch horizontally
          screenP = vec2(centeredUV.x * aspect, centeredUV.y);
        } else {
          // Portrait: stretch vertically
          screenP = vec2(centeredUV.x, centeredUV.y / aspect);
        }
        
        // Scale up to match the original shader's coordinate system
        screenP *= 2.0;
        
        vec2 wCoord = vec2(FC.x / r.x, FC.y / r.y);
        float waterHeight = texture2D(u_waterTexture, wCoord).r;
        float waterInfluence = clamp(waterHeight * u_waterStrength, -0.5, 0.5);
        
        float baseRadius = 0.9;
        float audioPulse = u_audioOverall * u_audioReactivity * 0.1;
        float waterPulse = waterInfluence * 0.3;
        float circleRadius = baseRadius + audioPulse + waterPulse;
        
        float distFromCenter = length(screenP);
        float inCircle = smoothstep(circleRadius + 0.1, circleRadius - 0.1, distFromCenter);
        
        vec4 o = vec4(0.0);
        
        if (inCircle > 0.0) {
          vec2 p = screenP * 1.1;
          
          float rippleTime = u_time - u_ripple_time;
          vec2 ripplePos = u_ripple_position * r;
          float rippleDist = distance(FC.xy, ripplePos);
          
          float clickRipple = 0.0;
          if (rippleTime < 3.0 && rippleTime > 0.0) {
            float rippleRadius = rippleTime * 150.0;
            float rippleWidth = 30.0;
            float rippleDecay = 1.0 - rippleTime / 3.0;
            clickRipple = exp(-abs(rippleDist - rippleRadius) / rippleWidth) * rippleDecay * u_ripple_strength;
          }
          
          float totalWaterInfluence = clamp((waterInfluence + clickRipple * 0.1) * u_waterStrength, -0.8, 0.8);
          float audioInfluence = (u_audioLow * 0.3 + u_audioMid * 0.4 + u_audioHigh * 0.3) * u_audioReactivity;
          
          float angle = length(p) * 4.0 + audioInfluence * 2.0;
          mat2 R = mat2(cos(angle), -sin(angle), sin(angle), cos(angle));
          p *= R;
          
          float l = length(p) - 0.7 + totalWaterInfluence * 0.5 + audioInfluence * 0.2;
          float t = u_time * u_speed + totalWaterInfluence * 2.0 + audioInfluence * 1.5;
          float enhancedY = p.y + totalWaterInfluence * 0.3 + audioInfluence * 0.2;
          
          float pattern1 = 0.5 + 0.5 * tanh(0.1 / max(l / 0.1, -l) - sin(l + enhancedY * max(1.0, -l / 0.1) + t));
          float pattern2 = 0.5 + 0.5 * tanh(0.1 / max(l / 0.1, -l) - sin(l + enhancedY * max(1.0, -l / 0.1) + t + 1.0));
          float pattern3 = 0.5 + 0.5 * tanh(0.1 / max(l / 0.1, -l) - sin(l + enhancedY * max(1.0, -l / 0.1) + t + 2.0));
          
          float intensity = 1.0 + totalWaterInfluence * 0.5 + audioInfluence * 0.3;
          
          if (u_isMonochrome) {
            float mono = (pattern1 + pattern2 + pattern3) / 3.0 * intensity;
            o = vec4(mono, mono, mono, inCircle);
          } else {
            o.r = pattern1 * u_color1.r * intensity;
            o.g = pattern2 * u_color2.g * intensity;
            o.b = pattern3 * u_color3.b * intensity;
            o.a = inCircle;
          }
        }
        
        vec3 bgColor = u_isMonochrome ? vec3(0.0) : u_background;
        vec3 finalColor = bgColor;
        
        finalColor = mix(finalColor, o.rgb, o.a);
        
        if (u_showText) {
          vec2 waterCoords = vec2(FC.x / r.x, FC.y / r.y);
          float step = 1.0 / r.x;
          vec2 waterGrad = clamp(vec2(
            texture2D(u_waterTexture, vec2(waterCoords.x + step, waterCoords.y)).r - 
            texture2D(u_waterTexture, vec2(waterCoords.x - step, waterCoords.y)).r,
            texture2D(u_waterTexture, vec2(waterCoords.x, waterCoords.y + step)).r - 
            texture2D(u_waterTexture, vec2(waterCoords.x, waterCoords.y - step)).r
          ) * u_waterStrength, -0.1, 0.1);
          
          vec2 textDistortedUV = uv + waterGrad * 0.15;
          vec4 textColor = texture2D(u_textTexture, textDistortedUV);
          
          if (u_isMonochrome) {
            float textLum = dot(textColor.rgb, vec3(0.299, 0.587, 0.114));
            textColor = vec4(textLum, textLum, textLum, textColor.a);
          }
          
          finalColor = mix(finalColor, textColor.rgb, textColor.a);
        }
        
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `;

    materialRef.current = new THREE.ShaderMaterial({
  vertexShader,
  fragmentShader,
  uniforms: {
        u_time: { value: 0.0 },
        u_resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        u_speed: { value: 1.3 },
        u_color1: { value: new THREE.Vector3(1.0, 1.0, 1.0) },
        u_color2: { value: new THREE.Vector3(0.9, 0.95, 1.0) },
        u_color3: { value: new THREE.Vector3(0.8, 0.9, 1.0) },
        u_background: { value: new THREE.Vector3(0.02, 0.02, 0.05) },
        u_waterTexture: { value: waterTextureRef.current },
        u_waterStrength: { value: 0.55 },
        u_ripple_time: { value: -10.0 },
        u_ripple_position: { value: new THREE.Vector2(0.5, 0.5) },
        u_ripple_strength: { value: 0.5 },
        u_textTexture: { value: null },
        u_showText: { value: showText },
        u_isMonochrome: { value: false },
        u_audioLow: { value: 0.0 },
        u_audioMid: { value: 0.0 },
        u_audioHigh: { value: 0.0 },
        u_audioOverall: { value: 0.0 },
        u_audioReactivity: { value: 1.0 }
      }
    });

    // Create plane geometry that fills the entire viewport
    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, materialRef.current);
    sceneRef.current.add(mesh);
    cameraRef.current.position.z = 1;

    // Setup audio
    audioRef.current = new Audio();
    audioRef.current.src = "https://assets.codepen.io/7558/xor-is-epic-1446.mp3";
    audioRef.current.preload = "auto";
    audioRef.current.volume = 1.0;
    audioRef.current.crossOrigin = "anonymous";
    audioRef.current.addEventListener("ended", () => {
      if (isPlaying) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
    });
    audioRef.current.load();

    // Helper functions
    const createTextTexture = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { 
        alpha: true, 
        willReadFrequently: false,
        desynchronized: false
      });
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Use 4x resolution for ultra-sharp text
      const dpr = 4;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);
      
      // Enable best text rendering with sub-pixel rendering
      ctx.textRendering = "geometricPrecision";
      ctx.imageSmoothingEnabled = false;  // Disable smoothing for pixel-perfect text
      
      const fontSize = 0; // Set to 0 to disable canvas text rendering
      const maxWidth = 1000;
      const paragraphText = "Ich arbeite mit Visionären die etwas bewegen wollen um aus Ideen skalierbare Marken zu bauen";
      
      ctx.font = `700 ${fontSize}px sans-serif`;  // Disabled - text now rendered as HTML
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      
      // Word wrap logic
      const words = paragraphText.split(" ");
      const lines = [];
      let currentLine = "";
      
      words.forEach(word => {
        const testLine = currentLine + (currentLine ? " " : "") + word;
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });
      if (currentLine) {
        lines.push(currentLine);
      }
      
      const lineHeight = fontSize * 1.2;
      const totalTextHeight = lines.length * lineHeight;
      const startY = height / 2 - totalTextHeight / 2 + lineHeight / 2;
      
      lines.forEach((line, index) => {
        const y = startY + index * lineHeight;
        ctx.fillText(line, width / 2, y);
      });
      
      return canvas;
    };

    const setupTextTexture = () => {
      if (!showText) return;
        setTimeout(() => {
          const textCanvas = createTextTexture();
          const textTexture = new THREE.CanvasTexture(textCanvas);
          textTexture.flipY = true;
          textTexture.generateMipmaps = true;  // Enable mipmaps for better quality
          textTexture.minFilter = THREE.LinearMipmapLinearFilter;  // Best quality filtering
          textTexture.magFilter = THREE.LinearFilter;
          textTexture.anisotropy = 16;  // Maximum anisotropic filtering
          textTexture.needsUpdate = true;
          materialRef.current.uniforms.u_textTexture.value = textTexture;
        }, 100);
    };

    const updateWaterSimulation = () => {
      const { current, previous, velocity, vorticity } = waterBuffersRef.current;
      const { damping, resolution, tension } = waterSettingsRef.current;
      const safeTension = Math.min(tension, 0.05);
      const velocityDissipation = 0.08;
      const densityDissipation = 1.0;
      const vorticityInfluence = Math.min(Math.max(0.2, 0.0), 0.5);

  for (let i = 0; i < resolution * resolution * 2; i++) {
    velocity[i] *= 1.0 - velocityDissipation;
  }

  for (let i = 1; i < resolution - 1; i++) {
    for (let j = 1; j < resolution - 1; j++) {
      const index = i * resolution + j;
      const left = velocity[(index - 1) * 2 + 1];
      const right = velocity[(index + 1) * 2 + 1];
      const bottom = velocity[(index - resolution) * 2];
      const top = velocity[(index + resolution) * 2];
      vorticity[index] = (right - left - (top - bottom)) * 0.5;
    }
  }

  if (vorticityInfluence > 0.001) {
    for (let i = 1; i < resolution - 1; i++) {
      for (let j = 1; j < resolution - 1; j++) {
        const index = i * resolution + j;
        const velIndex = index * 2;
        const left = Math.abs(vorticity[index - 1]);
        const right = Math.abs(vorticity[index + 1]);
        const bottom = Math.abs(vorticity[index - resolution]);
        const top = Math.abs(vorticity[index + resolution]);
        const gradX = (right - left) * 0.5;
        const gradY = (top - bottom) * 0.5;
        const length = Math.sqrt(gradX * gradX + gradY * gradY) + 1e-5;
        const safeVorticity = Math.max(-1.0, Math.min(1.0, vorticity[index]));
            const forceX = (gradY / length) * safeVorticity * vorticityInfluence * 0.1;
            const forceY = (-gradX / length) * safeVorticity * vorticityInfluence * 0.1;
        velocity[velIndex] += Math.max(-0.1, Math.min(0.1, forceX));
        velocity[velIndex + 1] += Math.max(-0.1, Math.min(0.1, forceY));
      }
    }
  }

  for (let i = 1; i < resolution - 1; i++) {
    for (let j = 1; j < resolution - 1; j++) {
      const index = i * resolution + j;
      const velIndex = index * 2;
      const top = previous[index - resolution];
      const bottom = previous[index + resolution];
      const left = previous[index - 1];
      const right = previous[index + 1];
      current[index] = (top + bottom + left + right) / 2 - current[index];
          current[index] = current[index] * damping + previous[index] * (1 - damping);
      current[index] += (0 - previous[index]) * safeTension;
      const velMagnitude = Math.sqrt(
        velocity[velIndex] * velocity[velIndex] +
          velocity[velIndex + 1] * velocity[velIndex + 1]
      );
          const safeVelInfluence = Math.min(velMagnitude * 0.01, 0.1);
      current[index] += safeVelInfluence;
      current[index] *= 1.0 - densityDissipation * 0.01;
      current[index] = Math.max(-2.0, Math.min(2.0, current[index]));
    }
  }

  for (let i = 0; i < resolution; i++) {
    current[i] = 0;
    current[(resolution - 1) * resolution + i] = 0;
    velocity[i * 2] = 0;
    velocity[i * 2 + 1] = 0;
    velocity[((resolution - 1) * resolution + i) * 2] = 0;
    velocity[((resolution - 1) * resolution + i) * 2 + 1] = 0;
    current[i * resolution] = 0;
    current[i * resolution + (resolution - 1)] = 0;
    velocity[i * resolution * 2] = 0;
    velocity[i * resolution * 2 + 1] = 0;
    velocity[(i * resolution + (resolution - 1)) * 2] = 0;
    velocity[(i * resolution + (resolution - 1)) * 2 + 1] = 0;
  }

      [waterBuffersRef.current.current, waterBuffersRef.current.previous] = [
        waterBuffersRef.current.previous,
        waterBuffersRef.current.current
      ];
      waterTextureRef.current.image.data = waterBuffersRef.current.current;
      waterTextureRef.current.needsUpdate = true;
    };

    const addRipple = (screenX, screenY, strength = 1.0) => {
      const { resolution, rippleRadius } = waterSettingsRef.current;
      
      // Get actual canvas dimensions (not CSS dimensions)
      const canvas = rendererRef.current.domElement;
      const rect = canvas.getBoundingClientRect();
      
      // screenX/Y are relative to rect, convert to normalized 0-1
      const normalizedX = screenX / rect.width;
      const normalizedY = screenY / rect.height;
      
      // Map directly to texture coordinates (full 0-resolution range)
      const texX = Math.floor(normalizedX * resolution);
      const texY = Math.floor((1.0 - normalizedY) * resolution);  // Flip Y because screen Y=0 is top, texture Y=0 is bottom
      const radius = Math.max(rippleRadius, Math.floor(0.15 * resolution));  // Increased minimum radius
      const rippleStrength = strength * (waterSettingsRef.current.splatForce / 100000);  // Use splatForce setting
  const radiusSquared = radius * radius;

  for (let i = -radius; i <= radius; i++) {
    for (let j = -radius; j <= radius; j++) {
      const distanceSquared = i * i + j * j;
      if (distanceSquared <= radiusSquared) {
        const posX = texX + i;
        const posY = texY + j;
        if (posX >= 0 && posX < resolution && posY >= 0 && posY < resolution) {
          const index = posY * resolution + posX;
          const velIndex = index * 2;
          const distance = Math.sqrt(distanceSquared);
          const falloff = 1.0 - distance / radius;
          const rippleValue =
            Math.cos((distance / radius) * Math.PI * 0.5) *
            rippleStrength *
            falloff;
              waterBuffersRef.current.previous[index] += rippleValue;
          const angle = Math.atan2(j, i);
              const velocityStrength = rippleValue * 0.5;  // Increased from 0.2 to 0.5
              waterBuffersRef.current.velocity[velIndex] += Math.cos(angle) * velocityStrength;
              waterBuffersRef.current.velocity[velIndex + 1] += Math.sin(angle) * velocityStrength;
          const swirlAngle = angle + Math.PI * 0.5;
          const swirlStrength = Math.min(velocityStrength * 0.5, 0.3);  // Increased limits
              waterBuffersRef.current.velocity[velIndex] += Math.cos(swirlAngle) * swirlStrength;
              waterBuffersRef.current.velocity[velIndex + 1] += Math.sin(swirlAngle) * swirlStrength;
            }
          }
        }
      }
    };

    const updateAudioAnalysis = () => {
      if (analyserRef.current && dataArrayRef.current && isPlaying) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        const bassEnd = Math.floor(dataArrayRef.current.length * 0.1);
        const midEnd = Math.floor(dataArrayRef.current.length * 0.5);
        let bass = 0, mid = 0, treble = 0;
        
        for (let i = 0; i < bassEnd; i++) bass += dataArrayRef.current[i];
        bass = bass / bassEnd / 255;
        
        for (let i = bassEnd; i < midEnd; i++) mid += dataArrayRef.current[i];
        mid = mid / (midEnd - bassEnd) / 255;
        
        for (let i = midEnd; i < dataArrayRef.current.length; i++) treble += dataArrayRef.current[i];
        treble = treble / (dataArrayRef.current.length - midEnd) / 255;
        
        const overall = (bass + mid + treble) / 3;
        const smoothing = 0.8;
        
        materialRef.current.uniforms.u_audioLow.value =
          materialRef.current.uniforms.u_audioLow.value * smoothing + bass * (1 - smoothing);
        materialRef.current.uniforms.u_audioMid.value =
          materialRef.current.uniforms.u_audioMid.value * smoothing + mid * (1 - smoothing);
        materialRef.current.uniforms.u_audioHigh.value =
          materialRef.current.uniforms.u_audioHigh.value * smoothing + treble * (1 - smoothing);
        materialRef.current.uniforms.u_audioOverall.value =
          materialRef.current.uniforms.u_audioOverall.value * smoothing + overall * (1 - smoothing);
      }
    };

    // Event handlers
    const onMouseMove = (event) => {
      // Get canvas position and calculate relative coordinates
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const now = performance.now();
      
      if (now - mouseThrottleTimeRef.current < 8) return;
      mouseThrottleTimeRef.current = now;
      
      const dx = x - lastMousePositionRef.current.x;
      const dy = y - lastMousePositionRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const velocity = distance / 8;
      
      if (distance > 1) {
        const velocityInfluence = Math.min(velocity / 10, 2.0);
        const baseIntensity = Math.min(distance / 20, 1.0);
        const fluidIntensity = baseIntensity * velocityInfluence * waterSettingsRef.current.mouseIntensity * 2.5;  // Apply mouseIntensity setting
        const variation = Math.random() * 0.3 + 0.7;
        const finalIntensity = fluidIntensity * variation;
        const jitterX = x + (Math.random() - 0.5) * 3;
        const jitterY = y + (Math.random() - 0.5) * 3;
        
        addRipple(jitterX, jitterY, finalIntensity);
        lastMousePositionRef.current.x = x;
        lastMousePositionRef.current.y = y;
      }
    };

    const onMouseClick = (event) => {
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      addRipple(x, y, waterSettingsRef.current.clickIntensity * 2.0);  // Use clickIntensity setting
      
      // Normalize to 0-1 range for shader
      const clickX = x / rect.width;
      const clickY = y / rect.height;
      materialRef.current.uniforms.u_ripple_position.value.set(clickX, clickY);
      materialRef.current.uniforms.u_ripple_time.value = clockRef.current.getElapsedTime();
    };

    const onTouchMove = (event) => {
      const touch = event.touches[0];
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      
      // Calculate movement from touch start
      const dx = x - touchStartRef.current.x;
      const dy = y - touchStartRef.current.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      
      // If movement is primarily vertical (scrolling), allow it
      // Only prevent default if horizontal movement is dominant
      if (absDx > absDy && absDx > 10) {
        event.preventDefault();
      }
      
      const now = performance.now();
      
      if (now - mouseThrottleTimeRef.current < 8) return;
      mouseThrottleTimeRef.current = now;
      
      const deltaX = x - lastMousePositionRef.current.x;
      const deltaY = y - lastMousePositionRef.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const velocity = distance / 8;
      
      if (distance > 1) {
        const velocityInfluence = Math.min(velocity / 10, 2.0);
        const baseIntensity = Math.min(distance / 20, 1.0);
        const fluidIntensity = baseIntensity * velocityInfluence * waterSettingsRef.current.mouseIntensity * 2.5;  // Apply mouseIntensity setting
        const variation = Math.random() * 0.3 + 0.7;
        const finalIntensity = fluidIntensity * variation;
        const jitterX = x + (Math.random() - 0.5) * 3;
        const jitterY = y + (Math.random() - 0.5) * 3;
        
        addRipple(jitterX, jitterY, finalIntensity);
        lastMousePositionRef.current.x = x;
        lastMousePositionRef.current.y = y;
      }
    };

    const onTouchStart = (event) => {
      const touch = event.touches[0];
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      
      // Store initial touch position for movement detection
      touchStartRef.current = { x, y };
      lastMousePositionRef.current = { x, y };
      
      // Create a subtle ripple on touch start
      addRipple(x, y, waterSettingsRef.current.clickIntensity * 0.5);  // Reduced intensity
      
      // Normalize to 0-1 range for shader
      const clickX = x / rect.width;
      const clickY = y / rect.height;
      materialRef.current.uniforms.u_ripple_position.value.set(clickX, clickY);
      materialRef.current.uniforms.u_ripple_time.value = clockRef.current.getElapsedTime();
    };

    const onWindowResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      rendererRef.current.setSize(width, height);
      materialRef.current.uniforms.u_resolution.value.set(width, height);
      setupTextTexture();
    };

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      const elapsed = clockRef.current.getElapsedTime();
      materialRef.current.uniforms.u_time.value = elapsed;
      updateAudioAnalysis();
      updateWaterSimulation();
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };

    // Add event listeners
    const canvas = rendererRef.current.domElement;
    
    // Register mouse/touch events on canvas only, not on window
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("click", onMouseClick);
    canvas.addEventListener("touchmove", onTouchMove, { passive: false }); // passive: false allows selective preventDefault()
    canvas.addEventListener("touchstart", onTouchStart); // passive: true (default) for better scrolling performance
    
    // Window resize can stay on window
    window.addEventListener("resize", onWindowResize);

    // Initialize
    setupTextTexture();
    animate();

    // Initial ripple in the center
    setTimeout(() => {
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      addRipple(rect.width / 2, rect.height / 2, 1.5);
    }, 500);

    // Cleanup function
    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      
      // Remove canvas event listeners
      const canvas = rendererRef.current?.domElement;
      if (canvas) {
        canvas.removeEventListener("mousemove", onMouseMove);
        canvas.removeEventListener("click", onMouseClick);
        canvas.removeEventListener("touchmove", onTouchMove);
        canvas.removeEventListener("touchstart", onTouchStart);
      }
      
      // Remove window event listeners
      window.removeEventListener("resize", onWindowResize);
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [isPlaying, showText]);

  const handleAudioToggle = () => {
  if (!isPlaying) {
      // Initialize audio context
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;
        const bufferLength = analyserRef.current.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(bufferLength);
        sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
      }
      
      audioRef.current.play()
      .then(() => {
          setIsPlaying(true);
      })
      .catch((e) => {
        console.log("Audio play failed:", e);
      });
  } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  return (
    <div className="morph-background-container" ref={containerRef}>
      <div className="morph-background-controls">
        <button 
          className="morph-background-audio-btn" 
          onClick={handleAudioToggle}
        >
          {isPlaying ? '[ stop ]' : '[ play ]'}
        </button>
      </div>
      
      {showText && (
        <div className="morph-glass-text">
          <h1 className="morph-glass-headline">
            Ich arbeite mit Visionären die etwas bewegen wollen um aus Ideen skalierbare Marken zu bauen
          </h1>
        </div>
      )}
    </div>
  );
};

export default MorphBackground;
